import React from "react";
import { Box, CircularProgress, Paper, Typography } from "@mui/material";
import { format, parseISO } from "date-fns";

/**
 * The shared furniture of the two usage pages.
 *
 * `admin/UsagePage` (one organization's ChartMind activity) and
 * `global-admin/UsageMonitorPage` (platform-wide AI spend) answer different questions from
 * different collections, but they are the same kind of screen: a period control, a row of stat
 * cards, then panels holding a stacked daily bar chart and a table. They had drifted — the
 * control row was a bare Box on one page and an outlined Paper on the other, charts were 300px
 * and 320px, and StatCard was copied verbatim into both files. Anything defined here is
 * defined once, so the next divergence has to be deliberate.
 */

/** Height of a daily bar chart, shared so the two pages present at the same scale. */
export const CHART_HEIGHT = 320;

/**
 * A `YYYY-MM-DD` key as a short label.
 *
 * Date-only strings parse to local midnight and are formatted back to the same calendar date,
 * so this is safe for both the local keys ChartMind writes and the UTC keys the platform
 * rollups use — neither can be shifted into a neighbouring day by the reader's timezone.
 */
export const formatDayLabel = (dateKey) => format(parseISO(dateKey), "MMM d");

/** One headline number. Outlined, so the cards read as a row against the elevated panels. */
export const StatCard = ({ title, value, subtitle, icon, color = "primary" }) => (
  <Paper
    variant="outlined"
    sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column", gap: 0.5 }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {React.cloneElement(icon, { color, sx: { fontSize: 20 } })}
      <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.3 }}>
        {title}
      </Typography>
    </Box>
    <Typography variant="h5">{value}</Typography>
    {subtitle && (
      <Typography variant="body2" color="text.secondary">
        {subtitle}
      </Typography>
    )}
  </Paper>
);

/**
 * The period and view controls, above everything they filter.
 *
 * No surface of its own: the toggles and selects are already bounded controls, and a box drawn
 * around them only adds a second border a few pixels outside theirs. Children are laid out as
 * clusters — period on the left, view options on the right — so the two pages put the same
 * control in the same place.
 */
export const ControlBar = ({ children }) => (
  <Box
    sx={{
      display: "flex",
      flexWrap: "wrap",
      gap: 2,
      alignItems: "center",
      justifyContent: "space-between",
      mb: 3,
    }}
  >
    {children}
  </Box>
);

/** A panel of content: chart, table, or stat group. Elevated, against the outlined cards. */
export const UsagePanel = ({ children, sx }) => (
  <Paper sx={{ p: 3, ...sx }}>{children}</Paper>
);

/**
 * A panel's heading. Carries its own bottom margin, so panel bodies never add their own top
 * margin and the gap under a heading is the same on both pages whether or not there is a note.
 */
export const PanelHeader = ({ overline, title, note, action }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: { xs: "column", sm: "row" },
      justifyContent: "space-between",
      alignItems: { sm: action ? "center" : "flex-start" },
      gap: 2,
      mb: 2,
    }}
  >
    <Box>
      {overline && (
        <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
          {overline}
        </Typography>
      )}
      {title && <Typography variant="h6">{title}</Typography>}
      {note && (
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "48rem" }}>
          {note}
        </Typography>
      )}
    </Box>
    {action}
  </Box>
);

/** A heading for a whole zone of the page, above its panels rather than inside one. */
export const ZoneHeader = ({ overline, title, note }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
      {overline}
    </Typography>
    <Typography variant="h5">{title}</Typography>
    {note && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: "48rem" }}>
        {note}
      </Typography>
    )}
  </Box>
);

/**
 * The one loading indicator these pages use. `height` holds a chart's space so the page does
 * not jump when the data lands; without it the spinner just takes its own padding.
 */
export const PanelLoader = ({ height }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      ...(height ? { height } : { py: 6 }),
    }}
  >
    <CircularProgress size={28} />
  </Box>
);
